import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI, businessAPI, householdAPI } from '../../services/api';
import { normalizeEnglishDigitsInput, normalizePhoneInput, normalizeEmailInput, normalizeUsernameInput, normalizeAadhaarInput, isValidPhone, isValidAadhaar, isValidEmail, isValidUsername, phoneMessage, emailMessage } from '../../utils/validation';
import { LEGAL_EFFECTIVE_DATE, LEGAL_VERSION } from './LegalPage';

const BUSINESS_ACCESS_SCOPE_OPTIONS = [
  ['view', 'व्यवसाय आणि कर खाते पाहणे'],
  ['pay', 'व्यवसाय कर भरणे'],
  ['view_receipts', 'भरणा पावत्या पाहणे'],
  ['download_receipts', 'पावत्या डाउनलोड करणे'],
  ['manage_profile', 'परवानगी असलेली व्यवसाय माहिती अद्ययावत करणे'],
  ['manage_tax', 'करविषयक पत्रव्यवहार व्यवस्थापित करणे'],
  ['submit_documents', 'आवश्यक कागदपत्रे सादर करणे'],
  ['submit_tax_declaration', 'वार्षिक कर घोषणा सादर करणे'],
];

function EyeIcon({ show }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {show ? (
        <>
          <path d="m3 3 18 18" />
          <path d="M10.6 10.6A2 2 0 0 0 13.4 13.4" />
          <path d="M9.9 4.2A10 10 0 0 1 12 4c6.5 0 10 8 10 8a17 17 0 0 1-2.2 3.4M6.6 6.6C3.9 8.5 2 12 2 12s3.5 8 10 8a10 10 0 0 0 4.1-.8" />
        </>
      ) : (
        <>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  );
}

export default function RegisterPage() {
  const { tenantSlug } = useParams();
  const { register, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const requestedAccountKind = searchParams.get('type') === 'business' ? 'external_taxpayer' : 'citizen';

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate(`/gp/${tenantSlug}/dashboard`, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, tenantSlug]);

  const [formData, setFormData] = useState({
    full_name: '', email: '', username: '', password: '', confirmPassword: '',
    date_of_birth: '', gender: '',
    phone: '', aadhaar_number: '', household_id: '', relation_to_head: 'other',
    account_kind: requestedAccountKind, business_record_mode: 'existing',
    requested_business_uid: '', business_name: '', business_registration_number: '',
    business_type: 'retail', business_address: '', business_commenced_on: '',
    requested_business_relationship: 'owner', requested_access_scopes: ['view', 'pay'], external_address: '',
  });
  const [householdSearch, setHouseholdSearch] = useState('');
  const [households, setHouseholds] = useState([]);
  const [householdPickerOpen, setHouseholdPickerOpen] = useState(false);
  const [householdSearchLoading, setHouseholdSearchLoading] = useState(false);
  const [householdSearchAttempted, setHouseholdSearchAttempted] = useState(false);
  const [householdSearchError, setHouseholdSearchError] = useState('');
  const [businessMatches, setBusinessMatches] = useState([]);
  const [businessSearchLoading, setBusinessSearchLoading] = useState(false);
  const [aadhaarPhoto, setAadhaarPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [phoneVerification, setPhoneVerification] = useState({
    otp: '',
    sentPhone: '',
    verifiedPhone: '',
    token: '',
    busy: false,
    message: '',
  });
  const [legalConsent, setLegalConsent] = useState({
    terms: false,
    privacy: false,
    declaration: false,
  });

  const validationErrors = useMemo(() => {
    const errors = {};
    if (formData.full_name.trim().length < 3) errors.full_name = 'पूर्ण नाव किमान ३ अक्षरांचे असावे.';
    if (!isValidEmail(formData.email)) errors.email = emailMessage;
    if (!isValidUsername(formData.username)) {
      errors.username = 'वापरकर्ता नाव ३ ते १०० इंग्रजी अक्षरे/अंकांचे असावे. Space वापरता येणार नाही.';
    }
    if (!isValidPhone(formData.phone)) errors.phone = phoneMessage;
    else if (!phoneVerification.token || phoneVerification.verifiedPhone !== formData.phone) {
      errors.phone_verification = 'मोबाईल क्रमांक WhatsApp OTP द्वारे सत्यापित करा.';
    }
    if (formData.account_kind === 'citizen') {
      if (!formData.date_of_birth) errors.date_of_birth = 'जन्मतारीख निवडणे आवश्यक आहे.';
      else if (formData.date_of_birth > new Date().toISOString().slice(0, 10)) errors.date_of_birth = 'जन्मतारीख भविष्यातील असू शकत नाही.';
      if (!['male', 'female', 'other'].includes(formData.gender)) errors.gender = 'लिंग निवडणे आवश्यक आहे.';
    }
    if (!isValidAadhaar(formData.aadhaar_number)) errors.aadhaar_number = 'कृपया वैध १२ अंकी आधार क्रमांक टाका.';
    if (!aadhaarPhoto) errors.aadhaar_photo = 'पडताळणीसाठी आधार कार्डचा फोटो किंवा PDF आवश्यक आहे.';
    if (formData.password.length < 6) errors.password = 'पासवर्ड किमान ६ अक्षरांचा असावा.';
    if (!formData.confirmPassword) errors.confirmPassword = 'कृपया पासवर्ड पुन्हा टाका.';
    else if (formData.password !== formData.confirmPassword) errors.confirmPassword = 'दोन्ही पासवर्ड जुळत नाहीत.';

    if (formData.account_kind === 'external_taxpayer') {
      if (!formData.business_name.trim() && !formData.business_registration_number.trim()) {
        errors.business_name = 'व्यवसायाचे नाव किंवा नोंदणी/परवाना क्रमांक आवश्यक आहे.';
      }
      if (formData.business_record_mode === 'new' && !formData.business_type) {
        errors.business_type = 'व्यवसाय प्रकार निवडा.';
      }
      if (formData.business_record_mode === 'new' && !formData.business_address.trim()) {
        errors.business_address = 'ग्रामपंचायत हद्दीतील व्यवसायाचा पत्ता आवश्यक आहे.';
      }
      if (!formData.requested_access_scopes.length) {
        errors.requested_access_scopes = 'किमान एक व्यवसाय प्रवेश सुविधा निवडा.';
      }
    }

    if (!legalConsent.terms) errors.terms = 'अटी व शर्ती स्वीकारणे आवश्यक आहे.';
    if (!legalConsent.privacy) errors.privacy = 'गोपनीयता धोरण स्वीकारणे आवश्यक आहे.';
    if (!legalConsent.declaration) errors.declaration = 'माहितीची घोषणा स्वीकारणे आवश्यक आहे.';
    return errors;
  }, [formData, aadhaarPhoto, legalConsent, phoneVerification.token, phoneVerification.verifiedPhone]);

  const touchField = name => setTouchedFields(current => ({ ...current, [name]: true }));
  const fieldError = name => touchedFields[name] ? validationErrors[name] : '';
  const validationProps = name => ({
    onBlur: () => touchField(name),
    'aria-invalid': Boolean(fieldError(name)),
    'aria-describedby': fieldError(name) ? `${name}-error` : undefined,
  });
  const renderFieldError = name => fieldError(name)
    ? <span id={`${name}-error`} className="reg-field-error" role="alert">{fieldError(name)}</span>
    : null;

  useEffect(() => {
    if (!tenantSlug || formData.account_kind !== 'citizen') return;
    const search = householdSearch.trim();
    if (formData.household_id) return;
    if (search.length < 4) {
      setHouseholds([]);
      setHouseholdSearchLoading(false);
      setHouseholdSearchAttempted(false);
      setHouseholdSearchError('');
      return;
    }
    const timer = setTimeout(() => {
      setHouseholdSearchLoading(true);
      setHouseholdSearchError('');
      householdAPI.publicLookup(tenantSlug, { search, limit: 10 })
        .then(res => {
          setHouseholds(res.data.households || []);
          setHouseholdSearchAttempted(true);
        })
        .catch(err => {
          setHouseholds([]);
          setHouseholdSearchAttempted(true);
          setHouseholdSearchError(err.response?.data?.error || 'कुटुंब शोधता आले नाही. कृपया पुन्हा प्रयत्न करा.');
        })
        .finally(() => setHouseholdSearchLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [tenantSlug, householdSearch, formData.account_kind, formData.household_id]);

  useEffect(() => {
    if (!tenantSlug || formData.account_kind !== 'external_taxpayer'
      || formData.business_record_mode !== 'existing' || formData.requested_business_uid
      || formData.business_name.trim().length < 3) {
      setBusinessMatches([]);
      return;
    }
    const timer = setTimeout(() => {
      setBusinessSearchLoading(true);
      businessAPI.getPublic(tenantSlug, { q: formData.business_name.trim(), limit: 8 })
        .then(response => setBusinessMatches(Array.isArray(response.data) ? response.data : []))
        .catch(() => setBusinessMatches([]))
        .finally(() => setBusinessSearchLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [
    tenantSlug,
    formData.account_kind,
    formData.business_record_mode,
    formData.business_name,
    formData.requested_business_uid,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'account_kind') {
      setFormData(current => ({
        ...current,
        account_kind: value,
        household_id: '',
        relation_to_head: value === 'citizen' ? 'other' : '',
      }));
      setHouseholdSearch('');
      setHouseholdPickerOpen(false);
      setHouseholdSearchAttempted(false);
      setHouseholdSearchError('');
      return;
    }
    if (name === 'business_record_mode') {
      setFormData(current => ({
        ...current,
        business_record_mode: value,
        requested_business_uid: '',
        business_name: '',
        business_registration_number: '',
      }));
      setBusinessMatches([]);
      return;
    }
    if (name === 'business_name') {
      setFormData(current => ({
        ...current,
        business_name: value,
        requested_business_uid: '',
      }));
      return;
    }
    // For aadhaar_number, only allow digits and max 12
    if (name === 'aadhaar_number') {
      const digits = normalizeAadhaarInput(value);
      setFormData({ ...formData, [name]: digits });
      return;
    }
    // For phone, only allow digits and max 10
    if (name === 'phone') {
      const digits = normalizePhoneInput(value);
      setFormData({ ...formData, [name]: digits });
      if (digits !== phoneVerification.verifiedPhone || digits !== phoneVerification.sentPhone) {
        setPhoneVerification(current => ({
          ...current,
          otp: '',
          sentPhone: '',
          verifiedPhone: '',
          token: '',
          message: '',
        }));
      }
      return;
    }
    if (name === 'email') {
      setFormData({ ...formData, [name]: normalizeEmailInput(value) });
      return;
    }
    if (name === 'username') {
      setFormData({ ...formData, [name]: normalizeUsernameInput(value) });
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    touchField('aadhaar_photo');
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('फाइल आकार 5MB पेक्षा कमी असावा');
        return;
      }
      if (!['image/jpeg', 'image/png', 'application/pdf'].includes(file.type)) {
        setError('फक्त JPG, PNG किंवा PDF फाइल अपलोड करा');
        return;
      }
      setAadhaarPhoto(file);
      if (file.type.startsWith('image/')) {
        setPhotoPreview(URL.createObjectURL(file));
      } else {
        setPhotoPreview(null);
      }
    }
  };

  const formatAadhaar = (num) => {
    // Display as XXXX XXXX XXXX
    return num.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  };

  const requestRegistrationOtp = async () => {
    touchField('phone');
    touchField('phone_verification');
    setError('');
    if (!isValidPhone(formData.phone)) return;
    setPhoneVerification(current => ({ ...current, busy: true, message: '' }));
    try {
      await authAPI.requestRegistrationOtp({
        phone: formData.phone,
        full_name: formData.full_name,
        tenant_slug: tenantSlug,
      });
      setPhoneVerification(current => ({
        ...current,
        otp: '',
        sentPhone: formData.phone,
        verifiedPhone: '',
        token: '',
        busy: false,
        message: 'WhatsApp वर ६ अंकी OTP पाठवला आहे. तो ५ मिनिटांसाठी वैध आहे.',
      }));
    } catch (err) {
      const alreadyRegistered = err.response?.data?.code === 'PHONE_ALREADY_REGISTERED';
      const message = alreadyRegistered
        ? 'हा मोबाईल क्रमांक आधीच नोंदणीकृत आहे. कृपया विद्यमान खात्याने लॉगिन करा.'
        : (err.response?.data?.error || 'OTP पाठवता आला नाही. कृपया पुन्हा प्रयत्न करा.');
      setPhoneVerification(current => ({ ...current, busy: false, message }));
      setError(message);
    }
  };

  const unlockVerifiedPhone = () => {
    setPhoneVerification(current => ({
      ...current,
      otp: '',
      sentPhone: '',
      verifiedPhone: '',
      token: '',
      message: '',
    }));
    requestAnimationFrame(() => document.querySelector('[name="phone"]')?.focus());
  };

  const verifyRegistrationOtp = async () => {
    touchField('phone_verification');
    setError('');
    if (phoneVerification.sentPhone !== formData.phone || !/^\d{6}$/.test(phoneVerification.otp)) {
      setPhoneVerification(current => ({ ...current, message: 'कृपया वैध ६ अंकी OTP भरा.' }));
      return;
    }
    setPhoneVerification(current => ({ ...current, busy: true, message: '' }));
    try {
      const response = await authAPI.verifyRegistrationOtp({
        phone: formData.phone,
        otp: phoneVerification.otp,
        tenant_slug: tenantSlug,
      });
      setPhoneVerification(current => ({
        ...current,
        verifiedPhone: formData.phone,
        token: response.data.phone_verification_token,
        busy: false,
        message: 'मोबाईल क्रमांक यशस्वीरित्या सत्यापित झाला.',
      }));
    } catch (err) {
      setPhoneVerification(current => ({
        ...current,
        busy: false,
        token: '',
        verifiedPhone: '',
        message: err.response?.data?.error || 'OTP पडताळणी अयशस्वी झाली.',
      }));
    }
  };

  const toggleRequestedScope = scope => {
    setFormData(current => ({
      ...current,
      requested_access_scopes: current.requested_access_scopes.includes(scope)
        ? current.requested_access_scopes.filter(item => item !== scope)
        : [...current.requested_access_scopes, scope],
    }));
  };

  const formatHouseholdLabel = (household) => {
    if (!household) return '';
    const ward = household.ward_number ? `, प्रभाग ${household.ward_number}` : '';
    return `${household.household_uid} - ${household.head_name}${ward}`;
  };

  const selectHousehold = (household) => {
    if (!household) {
      setFormData(prev => ({ ...prev, household_id: '' }));
      setHouseholdSearch('');
      setHouseholdPickerOpen(false);
      return;
    }

    setFormData(prev => ({ ...prev, household_id: String(household.id) }));
    setHouseholdSearch(formatHouseholdLabel(household));
    setHouseholdPickerOpen(false);
    setHouseholdSearchAttempted(false);
    setHouseholdSearchError('');
  };

  const selectBusiness = business => {
    setFormData(current => ({
      ...current,
      requested_business_uid: business?.business_uid || '',
      business_name: business?.business_name || '',
    }));
    setBusinessMatches([]);
  };

  const handleHouseholdSearchChange = (value) => {
    setHouseholdSearch(value);
    setHouseholdPickerOpen(true);
    setFormData(prev => ({ ...prev, household_id: '' }));
    setHouseholdSearchError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const invalidFields = Object.keys(validationErrors);
    if (invalidFields.length) {
      setTouchedFields(invalidFields.reduce((fields, name) => ({ ...fields, [name]: true }), {}));
      setError('कृपया खाली दर्शवलेल्या चुका दुरुस्त करा.');
      requestAnimationFrame(() => {
        const firstInvalid = document.querySelector(`[name="${invalidFields[0]}"]`)
          || document.getElementById(`${invalidFields[0]}-error`);
        firstInvalid?.focus?.();
        firstInvalid?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      });
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('full_name', formData.full_name);
      submitData.append('email', formData.email);
      submitData.append('username', formData.username);
      submitData.append('password', formData.password);
      submitData.append('phone', formData.phone);
      submitData.append('phone_verification_token', phoneVerification.token);
      if (formData.account_kind === 'citizen') {
        submitData.append('date_of_birth', formData.date_of_birth);
        submitData.append('gender', formData.gender);
      }
      submitData.append('aadhaar_number', formData.aadhaar_number);
      submitData.append('account_kind', formData.account_kind);
      if (formData.account_kind === 'citizen') {
        if (formData.household_id) submitData.append('household_id', formData.household_id);
        submitData.append('relation_to_head', formData.relation_to_head);
      } else {
        submitData.append('business_name', formData.business_name);
        submitData.append('business_record_mode', formData.business_record_mode);
        if (formData.requested_business_uid) submitData.append('requested_business_uid', formData.requested_business_uid);
        submitData.append('business_registration_number', formData.business_registration_number);
        submitData.append('business_type', formData.business_type);
        submitData.append('business_address', formData.business_address);
        submitData.append('business_commenced_on', formData.business_commenced_on);
        submitData.append('requested_business_relationship', formData.requested_business_relationship);
        submitData.append('requested_access_scopes', JSON.stringify(formData.requested_access_scopes));
        submitData.append('external_address', formData.external_address);
      }
      submitData.append('tenant_slug', tenantSlug);
      submitData.append('terms_accepted', String(legalConsent.terms));
      submitData.append('privacy_accepted', String(legalConsent.privacy));
      submitData.append('declaration_accepted', String(legalConsent.declaration));
      submitData.append('legal_version', LEGAL_VERSION);
      submitData.append('legal_effective_date', LEGAL_EFFECTIVE_DATE);
      submitData.append('aadhaar_photo', aadhaarPhoto);

      await register(submitData);
      setSuccess(true);
    } catch (err) {
      if (err.response?.data?.code === 'PHONE_OTP_VERIFICATION_REQUIRED') {
        setPhoneVerification(current => ({
          ...current,
          token: '',
          verifiedPhone: '',
          message: 'OTP पडताळणीची मुदत संपली आहे. कृपया नवीन OTP मागवा.',
        }));
        touchField('phone_verification');
      }
      setError(err.response?.data?.error || 'नोंदणी अयशस्वी. कृपया पुन्हा प्रयत्न करा.');
    } finally {
      setLoading(false);
    }
  };

  // Responsive styles injected via <style> tag
  const css = `
    .reg-page {
      min-height: calc(100vh - 120px);
      padding: 28px 0 40px;
      background:
        linear-gradient(115deg, rgba(246, 251, 255, 0.92) 0%, rgba(238, 247, 255, 0.86) 42%, rgba(226, 241, 255, 0.34) 62%, rgba(255, 255, 255, 0.08) 100%),
        var(--reg-bg-image) center bottom / cover no-repeat;
    }
    .reg-container { max-width: 1180px; margin: 0 auto; padding: 0 18px; }
    .reg-header {
      background: linear-gradient(135deg, #1a237e 0%, #1565c0 50%, #0288d1 100%);
      border-radius: 10px; padding: 14px 16px; margin-bottom: 16px;
      display: flex; align-items: center; gap: 14px; color: #fff;
      box-shadow: 0 3px 12px rgba(21,101,192,0.2);
    }
    .reg-header-icon { font-size: 28px; flex-shrink: 0; }
    .reg-header h1 { margin: 0; font-size: 18px; font-weight: 700; }
    .reg-header p { margin: 2px 0 0; font-size: 12px; opacity: 0.85; }
    .reg-badge {
      margin-left: auto; background: rgba(255,255,255,0.15); border-radius: 14px;
      padding: 3px 10px; font-size: 11px; white-space: nowrap; flex-shrink: 0;
    }
    .reg-card { background: #fff; border: 1px solid #e0e0e0; border-radius: 10px; padding: 18px; }
    .reg-row { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0 16px; }
    .reg-account-row { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .reg-row .form-group { margin-bottom: 12px; }
    .reg-row .form-group label { font-size: 13px; margin-bottom: 3px; display: block; }
    .reg-row .form-group input, .reg-row .form-group select { padding: 8px 10px; font-size: 14px; width: 100%; }
    .reg-row .form-group input.reg-english-input {
      direction: ltr; text-align: left; unicode-bidi: isolate; font-variant-numeric: tabular-nums;
    }
    .reg-input-wrap {
      position: relative; display: flex; align-items: center; width: 100%;
    }
    .reg-input-wrap input {
      width: 100%; padding-right: 38px !important;
    }
    .reg-input-wrap input::-ms-reveal,
    .reg-input-wrap input::-ms-clear {
      display: none !important; width: 0 !important; height: 0 !important;
    }
    .reg-password-toggle {
      position: absolute; right: 4px; top: 50%; transform: translateY(-50%);
      display: grid; width: 32px; height: 32px; padding: 0; place-items: center;
      border: 0; border-radius: 6px; background: transparent; color: #64748b;
      cursor: pointer; transition: color 0.15s ease, background 0.15s ease;
    }
    .reg-password-toggle:hover, .reg-password-toggle:focus-visible {
      color: #1e3a8a; background: #f1f5f9; outline: none;
    }
    .reg-field-error {
      display: block; margin-top: 5px; color: #b42318; font-size: 12px;
      font-weight: 600; line-height: 1.35;
    }
    .reg-card [aria-invalid="true"] {
      border-color: #d92d20 !important; box-shadow: 0 0 0 2px rgba(217, 45, 32, 0.1);
    }
    .reg-upload-zone[data-invalid="true"] {
      border-color: #d92d20 !important; background: #fff6f5;
    }
    .reg-phone-otp { display: flex; gap: 7px; margin-top: 7px; align-items: stretch; }
    .reg-phone-otp input { min-width: 0; flex: 1; letter-spacing: .2em; }
    .reg-phone-otp button {
      flex: 0 0 auto; border: 1px solid #1672ad; border-radius: 7px; padding: 7px 11px;
      background: #eef8ff; color: #075f98; font-size: 12px; font-weight: 700; cursor: pointer;
    }
    .reg-phone-otp button:disabled { cursor: not-allowed; opacity: .55; }
    .reg-phone-verified {
      display: flex; margin-top: 6px; align-items: center; justify-content: space-between;
      gap: 8px; color: #087443; font-size: 12px; font-weight: 700;
    }
    .reg-phone-change {
      border: 0; padding: 2px 0; background: transparent; color: #075f98;
      font-size: 11px; font-weight: 700; text-decoration: underline; cursor: pointer;
    }
    .reg-phone-otp-message { display: block; margin-top: 5px; color: #52677c; font-size: 11px; line-height: 1.35; }
    .reg-household-card {
      background: #f8fbff; border: 1px solid #dbeafe; border-radius: 8px;
      padding: 10px; color: #0f172a; font-size: 12px; line-height: 1.5;
    }
    .reg-household-combobox { position: relative; }
    .reg-household-field { position: relative; display: flex; align-items: center; }
    .reg-household-field input { padding-right: 82px !important; }
    .reg-household-clear {
      position: absolute; right: 38px; top: 50%; transform: translateY(-50%);
      border: 0; background: transparent; color: #64748b; cursor: pointer;
      font-size: 18px; line-height: 1; padding: 4px;
    }
    .reg-household-toggle {
      position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
      border: 0; background: transparent; color: #0f3b72; cursor: pointer;
      font-size: 14px; padding: 4px;
    }
    .reg-household-menu {
      position: absolute; z-index: 30; left: 0; right: 0; top: calc(100% + 6px);
      max-height: 250px; overflow: auto; padding: 6px;
      border: 1px solid #cbd8e8; border-radius: 10px; background: #fff;
      box-shadow: 0 18px 38px rgba(15, 43, 82, 0.16);
    }
    .reg-household-option {
      width: 100%; border: 0; border-radius: 8px; background: transparent;
      padding: 9px 10px; text-align: left; cursor: pointer; color: #0f2f55;
      display: block;
    }
    .reg-household-option:hover { background: #edf6ff; }
    .reg-household-option strong { display: block; font-size: 13px; }
    .reg-household-option span { display: block; margin-top: 2px; font-size: 11px; color: #64748b; }
    .reg-household-option.none { color: #7c2d12; background: #fff7ed; margin-bottom: 4px; }
    .reg-household-empty { padding: 10px; color: #64748b; font-size: 12px; }
    .reg-section-title {
      font-size: 13px; font-weight: 600; color: #1565c0; margin: 0 0 10px;
      padding-bottom: 6px; border-bottom: 2px solid #e3f2fd;
      display: flex; align-items: center; gap: 6px;
    }
    .reg-section { margin-bottom: 14px; }
    .reg-aadhaar-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0 14px; align-items: start; }
    .reg-upload-zone {
      border: 2px dashed #ccc; border-radius: 8px; padding: 12px;
      text-align: center; cursor: pointer; background: #fafafa;
      transition: border-color 0.2s; min-height: 80px;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
    }
    .reg-upload-zone:hover { border-color: #1565c0; }
    .reg-notice {
      background: #fff8e1; border: 1px solid #ffecb3; border-radius: 6px;
      padding: 8px 10px; margin-bottom: 12px; font-size: 11px; line-height: 1.5;
    }
    .reg-submit { width: 100%; padding: 10px; font-size: 15px; }
    .reg-footer { text-align: center; margin-top: 12px; font-size: 13px; }
    @media (max-width: 1000px) {
      .reg-container { max-width: 820px; }
      .reg-row { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    }
    @media (max-width: 600px) {
      .reg-container { padding: 0 8px; }
      .reg-header { padding: 10px 12px; gap: 10px; }
      .reg-header h1 { font-size: 15px; }
      .reg-header p { font-size: 11px; }
      .reg-badge { display: none; }
      .reg-card { padding: 14px 12px; }
      .reg-row { grid-template-columns: 1fr; gap: 0; }
      .reg-account-row { grid-template-columns: 1fr; }
      .reg-aadhaar-row { grid-template-columns: 1fr; gap: 0; }
      .reg-row .form-group label { font-size: 12px; }
    }
  `;

  if (success) {
    return (
      <>
        <style>{css}</style>
        <div className="reg-page" style={{ '--reg-bg-image': `url(${process.env.PUBLIC_URL || ''}/HeaderBgImages/17.png)` }}>
        <div className="reg-container">
          <div className="reg-card" style={{ textAlign: 'center', padding: '30px 20px' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
            <h2 style={{ color: '#2e7d32', marginBottom: 8, fontSize: 20 }}>नोंदणी अर्ज यशस्वीपणे सादर झाला</h2>
            <p style={{ color: '#555', fontSize: 14, lineHeight: 1.5, marginBottom: 6 }}>
              तुमची नोंदणी यशस्वीरित्या सबमिट केली आहे. ग्रामपंचायत प्रशासनाकडून मंजूर झाल्यावर लॉगिन करू शकता.
            </p>
            <p style={{ color: '#555', fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>
              {formData.account_kind === 'external_taxpayer'
                ? 'तुमच्यासाठी गावातील नागरिक नोंद तयार केलेली नाही. व्यवसाय आणि तुमचे नाते पडताळल्यानंतर व्यवसाय करदाता प्रवेश मंजूर केला जाईल.'
                : 'ग्रामपंचायत प्रशासनाकडून नागरिक नोंदणी मंजूर झाल्यानंतर लॉगिन करता येईल.'}
            </p>
            <div style={{ background: '#fff3e0', border: '1px solid #ffe0b2', borderRadius: 6, padding: 10, marginBottom: 16 }}>
              <p style={{ margin: 0, color: '#e65100', fontSize: 12 }}>
                ⏳ अर्जाची स्थिती जाणून घेण्यासाठी ग्रामपंचायत कार्यालयाशी संपर्क साधता येईल.
              </p>
            </div>
            <Link to="/login" className="btn btn-primary" style={{ textDecoration: 'none', padding: '8px 28px' }}>
              लॉगिन पृष्ठावर जा
            </Link>
          </div>
        </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div className="reg-page" style={{ '--reg-bg-image': `url(${process.env.PUBLIC_URL || ''}/HeaderBgImages/17.png)` }}>
      <div className="reg-container">
        {/* Compact Header */}
        <div className="reg-header">
          <span className="reg-header-icon">🏛️</span>
          <div>
            <h1>ग्रामपंचायत पोर्टल खाते नोंदणी</h1>
            <p>गावातील नागरिक आणि गावाबाहेरील व्यवसायधारकांसाठी स्वतंत्र, पडताळणी-आधारित प्रवेश.</p>
          </div>
          <span className="reg-badge">🔒 सुरक्षित</span>
        </div>

        <div className="reg-card">
          {error && <div className="alert alert-error" style={{ marginBottom: 14, fontSize: 13 }}>{error}</div>}

          <form onSubmit={handleSubmit} noValidate>
            <div className="reg-section">
              <div className="reg-section-title">
                <span>🔗</span> Account purpose / खाते प्रकार
              </div>
              <div className="reg-row reg-account-row">
                <label className="reg-household-card" style={{ cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="account_kind"
                    value="citizen"
                    checked={formData.account_kind === 'citizen'}
                    onChange={handleChange}
                    style={{ marginRight: 8 }}
                  />
                  <strong>गावातील नागरिक</strong><br />
                  नागरिक व कुटुंब नोंदीशी खाते जोडण्यासाठी.
                </label>
                <label className="reg-household-card" style={{ cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="account_kind"
                    value="external_taxpayer"
                    checked={formData.account_kind === 'external_taxpayer'}
                    onChange={handleChange}
                    style={{ marginRight: 8 }}
                  />
                  <strong>गावाबाहेरील व्यवसाय करदाता</strong><br />
                  या ग्रामपंचायत हद्दीत व्यवसाय आहे, पण गावातील नागरिक नोंद नाही.
                </label>
              </div>
            </div>

            {/* Personal Information — 2-col grid */}
            <div className="reg-section">
              <div className="reg-section-title">
                <span>👤</span> वैयक्तिक माहिती
              </div>
              <div className="reg-row">
                <div className="form-group">
                  <label>पूर्ण नाव <span style={{ color: '#e53935' }}>*</span></label>
                  <input type="text" name="full_name" value={formData.full_name} onChange={handleChange} {...validationProps('full_name')} required placeholder="तुमचे पूर्ण नाव टाका" />
                  {renderFieldError('full_name')}
                </div>
                <div className="form-group">
                  <label>ईमेल <span style={{ color: '#e53935' }}>*</span></label>
                  <input className="reg-english-input" data-marathi-input="false" lang="en" dir="ltr" type="email" inputMode="email" autoCapitalize="none" spellCheck={false} autoComplete="email" name="email" value={formData.email} onChange={handleChange} {...validationProps('email')} required placeholder="tumcha@email.com" />
                  {renderFieldError('email')}
                </div>
                <div className="form-group">
                  <label>वापरकर्ता नाव <span style={{ color: '#e53935' }}>*</span></label>
                  <input className="reg-english-input" data-marathi-input="false" lang="en" dir="ltr" type="text" inputMode="text" autoCapitalize="none" spellCheck={false} autoComplete="username" name="username" value={formData.username} onChange={handleChange} {...validationProps('username')} required minLength={3} maxLength={100} pattern="[A-Za-z0-9._@-]{3,100}" title="Use only English letters, numbers, dot, underscore, @ or hyphen. Spaces are not allowed." placeholder="e.g. mukul.singh" />
                  {renderFieldError('username')}
                </div>
                <div className="form-group">
                  <label>फोन <span style={{ color: '#e53935' }}>*</span></label>
                  <input className="reg-english-input" data-marathi-input="false" lang="en" dir="ltr" type="tel" inputMode="numeric" autoComplete="tel" name="phone" value={formData.phone} onChange={handleChange} {...validationProps('phone')} disabled={Boolean(phoneVerification.token && phoneVerification.verifiedPhone === formData.phone)} required placeholder="10 अंकी फोन नंबर" maxLength={10} />
                  {renderFieldError('phone')}
                  {phoneVerification.token && phoneVerification.verifiedPhone === formData.phone ? (
                    <span className="reg-phone-verified">
                      <span>✓ मोबाईल क्रमांक सत्यापित</span>
                      <button type="button" className="reg-phone-change" onClick={unlockVerifiedPhone}>क्रमांक बदला</button>
                    </span>
                  ) : (
                    <>
                      {phoneVerification.sentPhone === formData.phone ? (
                        <div className="reg-phone-otp">
                          <input
                            className="reg-english-input"
                            data-marathi-input="false"
                            lang="en"
                            dir="ltr"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            name="registration_otp"
                            value={phoneVerification.otp}
                            onChange={event => setPhoneVerification(current => ({
                              ...current,
                              otp: normalizeEnglishDigitsInput(event.target.value, 6),
                            }))}
                            placeholder="६ अंकी OTP"
                            maxLength={6}
                            required
                            aria-label="Registration OTP"
                          />
                          <button type="button" onClick={verifyRegistrationOtp} disabled={phoneVerification.busy}>
                            {phoneVerification.busy ? 'पडताळत आहे…' : 'OTP पडताळा'}
                          </button>
                          <button type="button" onClick={requestRegistrationOtp} disabled={phoneVerification.busy}>
                            पुन्हा पाठवा
                          </button>
                        </div>
                      ) : (
                        <div className="reg-phone-otp">
                          <button type="button" onClick={requestRegistrationOtp} disabled={phoneVerification.busy || !isValidPhone(formData.phone)}>
                            {phoneVerification.busy ? 'पाठवत आहे…' : 'WhatsApp OTP पाठवा'}
                          </button>
                        </div>
                      )}
                      {renderFieldError('phone_verification')}
                    </>
                  )}
                  {phoneVerification.message && <span className="reg-phone-otp-message" role="status">{phoneVerification.message}</span>}
                </div>
                {formData.account_kind === 'citizen' && (
                  <>
                    <div className="form-group">
                      <label>जन्मतारीख <span style={{ color: '#e53935' }}>*</span></label>
                      <input
                        type="date"
                        name="date_of_birth"
                        value={formData.date_of_birth}
                        onChange={handleChange}
                        {...validationProps('date_of_birth')}
                        max={new Date().toISOString().slice(0, 10)}
                        required
                      />
                      {renderFieldError('date_of_birth')}
                    </div>
                    <div className="form-group">
                      <label>लिंग <span style={{ color: '#e53935' }}>*</span></label>
                      <select name="gender" value={formData.gender} onChange={handleChange} {...validationProps('gender')} required>
                        <option value="">लिंग निवडा</option>
                        <option value="male">पुरुष</option>
                        <option value="female">स्त्री</option>
                        <option value="other">इतर</option>
                      </select>
                      {renderFieldError('gender')}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Household linkage */}
            {formData.account_kind === 'citizen' && (
            <div className="reg-section">
              <div className="reg-section-title">
                <span>🏠</span> कुटुंब लिंक करा
              </div>
              <div className="reg-row">
                <div className="form-group">
                  <label>कुटुंब निवडा</label>
                  <div className="reg-household-combobox">
                    <div className="reg-household-field">
                      <input
                        type="text"
                        role="combobox"
                        aria-expanded={householdPickerOpen}
                        aria-controls="registration-household-options"
                        aria-autocomplete="list"
                        value={householdSearch}
                        onChange={(e) => handleHouseholdSearchChange(e.target.value)}
                        onFocus={() => setHouseholdPickerOpen(true)}
                        onBlur={() => setTimeout(() => setHouseholdPickerOpen(false), 160)}
                        placeholder="कुटुंबप्रमुखाचे नाव, UID किंवा १० अंकी मोबाईल"
                        autoComplete="off"
                      />
                      {householdSearch && (
                        <button type="button" className="reg-household-clear" onMouseDown={(e) => e.preventDefault()} onClick={() => selectHousehold(null)}>×</button>
                      )}
                      <button type="button" className="reg-household-toggle" onMouseDown={(e) => e.preventDefault()} onClick={() => setHouseholdPickerOpen(open => !open)}>⌄</button>
                    </div>
                    {householdPickerOpen && (
                      <div className="reg-household-menu" id="registration-household-options" role="listbox">
                        <button type="button" role="option" aria-selected={false} className="reg-household-option none" onMouseDown={(e) => e.preventDefault()} onClick={() => selectHousehold(null)}>
                          <strong>माझे कुटुंब यादीत नाही / नंतर पडताळणी</strong>
                          <span>ग्रामपंचायत पडताळणीनंतर खाते कुटुंबाशी लिंक करू शकते.</span>
                        </button>
                        {householdSearchLoading ? (
                          <div className="reg-household-empty" role="status">कुटुंब शोधत आहे…</div>
                        ) : householdSearch.trim().length < 4 ? (
                          <div className="reg-household-empty">
                            कुटुंबप्रमुखाच्या नावाची/UID ची किमान ४ अक्षरे किंवा अचूक १० अंकी मोबाईल क्रमांक टाका.
                          </div>
                        ) : householdSearchError ? (
                          <div className="reg-household-empty" role="alert">{householdSearchError}</div>
                        ) : households.length > 0 ? households.map(h => (
                          <button key={h.id} type="button" role="option" aria-selected={String(h.id) === formData.household_id} className="reg-household-option" onMouseDown={(e) => e.preventDefault()} onClick={() => selectHousehold(h)}>
                            <strong>{h.household_uid} - {h.head_name}</strong>
                            <span>{[
                              h.head_phone_masked ? `मोबाईल ${h.head_phone_masked}` : '',
                              h.ward_number ? `प्रभाग ${h.ward_number}` : '',
                            ].filter(Boolean).join(' • ')}</span>
                          </button>
                        )) : householdSearchAttempted ? (
                          <div className="reg-household-empty">या नावाने किंवा UID ने कोणतेही सत्यापित कुटुंब आढळले नाही. शब्दलेखन तपासा किंवा “माझे कुटुंब यादीत नाही” निवडा.</div>
                        ) : (
                          <div className="reg-household-empty">शोधण्यासाठी किमान ४ अक्षरे टाका.</div>
                        )}
                      </div>
                    )}
                  </div>
                  {!formData.household_id && (
                    <small style={{ display: 'block', marginTop: 5, color: '#64748b', fontSize: 11 }}>
                      नाव/UID ची किमान ४ अक्षरे किंवा अचूक १० अंकी मोबाईल क्रमांक टाका.
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>कुटुंबप्रमुखाशी नाते</label>
                  <select name="relation_to_head" value={formData.relation_to_head} onChange={handleChange}>
                    <option value="self">स्वतः</option>
                    <option value="spouse">पती/पत्नी</option>
                    <option value="son">मुलगा</option>
                    <option value="daughter">मुलगी</option>
                    <option value="father">वडील</option>
                    <option value="mother">आई</option>
                    <option value="other">इतर</option>
                  </select>
                </div>
                <div className="reg-household-card">
                  कुटुंब निवडल्यास तुमचे खाते त्या कुटुंबाशी आणि कर/मालमत्ता नोंदींशी लिंक केले जाईल. चुकीचे कुटुंब दिसत असल्यास रिकामे ठेवा; ग्रामपंचायत पडताळणीनंतर लिंक करू शकते.
                </div>
              </div>
            </div>
            )}

            {formData.account_kind === 'external_taxpayer' && (
              <div className="reg-section">
                <div className="reg-section-title">
                  <span>🏪</span> व्यवसाय खाते आणि कर प्रवेश विनंती
                </div>
                <div className="reg-household-card" style={{ marginBottom: 12 }}>
                  <strong>ही नागरिक नोंदणी नाही.</strong><br />
                  ग्रामपंचायत तुमची ओळख, व्यवसाय आणि त्याच्याशी असलेले नाते तपासेल. मंजुरीनंतर तुम्हाला फक्त संबंधित व्यवसाय, मागण्या, भरणा आणि पावत्यांचा प्रवेश मिळेल.
                </div>
                <div className="reg-row" style={{ marginBottom: 12 }}>
                  <label className="reg-household-card" style={{ cursor: 'pointer' }}>
                    <input type="radio" name="business_record_mode" value="existing" checked={formData.business_record_mode === 'existing'} onChange={handleChange} />
                    <strong> व्यवसाय आधीच GP नोंदवहीत आहे</strong><br />
                    नावाने शोधून अचूक व्यवसाय निवडा.
                  </label>
                  <label className="reg-household-card" style={{ cursor: 'pointer' }}>
                    <input type="radio" name="business_record_mode" value="new" checked={formData.business_record_mode === 'new'} onChange={handleChange} />
                    <strong> व्यवसाय नोंदवहीत दिसत नाही</strong><br />
                    नवीन व्यवसाय नोंदणी व खाते-जोडणीची संयुक्त विनंती करा.
                  </label>
                </div>
                <div className="reg-row">
                  <div className="form-group">
                    <label>व्यवसायाचे नाव <span style={{ color: '#e53935' }}>*</span></label>
                    <input
                      type="text"
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleChange}
                      {...validationProps('business_name')}
                      placeholder={formData.business_record_mode === 'existing' ? 'नोंदणीकृत व्यवसाय शोधा' : 'व्यवसायाचे अधिकृत किंवा प्रचलित नाव'}
                      required
                    />
                    {renderFieldError('business_name')}
                    {formData.business_record_mode === 'existing' && (
                      <div className="reg-household-menu" style={{ position: 'static', marginTop: 5 }}>
                        {businessSearchLoading && <div className="reg-household-empty">व्यवसाय शोधत आहे…</div>}
                        {!businessSearchLoading && businessMatches.map(business => (
                          <button key={business.business_uid} type="button" className="reg-household-option" onClick={() => selectBusiness(business)}>
                            <strong>{business.business_name}</strong>
                            <span>{[
                              business.business_type,
                              business.locations?.find(location => location.is_primary)?.address_line_1 || business.address,
                            ].filter(Boolean).join(' • ')}</span>
                          </button>
                        ))}
                        {!businessSearchLoading && formData.business_name.trim().length >= 3 && !businessMatches.length && !formData.requested_business_uid && (
                          <div className="reg-household-empty">नोंदवहीत व्यवसाय आढळला नाही. “व्यवसाय नोंदवहीत दिसत नाही” हा पर्याय निवडा.</div>
                        )}
                      </div>
                    )}
                    {formData.requested_business_uid && <small style={{ color: '#16704f' }}>✓ निवडलेला व्यवसाय: {formData.requested_business_uid}</small>}
                  </div>
                  <div className="form-group">
                    <label>नोंदणी / परवाना क्रमांक</label>
                    <input
                      type="text"
                      name="business_registration_number"
                      value={formData.business_registration_number}
                      onChange={handleChange}
                      placeholder="GST, Udyam, Shop Act किंवा स्थानिक परवाना"
                    />
                  </div>
                  {formData.business_record_mode === 'new' && <>
                    <div className="form-group">
                      <label>व्यवसाय प्रकार <span style={{ color: '#e53935' }}>*</span></label>
                      <select name="business_type" value={formData.business_type} onChange={handleChange} {...validationProps('business_type')} required>
                        <option value="retail">किरकोळ विक्री</option>
                        <option value="grocery">किराणा</option>
                        <option value="services">सेवा</option>
                        <option value="manufacturing">उत्पादन</option>
                        <option value="restaurant">हॉटेल / भोजनालय</option>
                        <option value="agriculture">कृषी व्यवसाय</option>
                        <option value="dairy">दुग्ध व्यवसाय</option>
                        <option value="other">इतर</option>
                      </select>
                      {renderFieldError('business_type')}
                    </div>
                    <div className="form-group">
                      <label>व्यवसाय सुरू झाल्याची तारीख</label>
                      <input type="date" name="business_commenced_on" value={formData.business_commenced_on} onChange={handleChange} />
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>ग्रामपंचायत हद्दीतील व्यवसायाचा पत्ता <span style={{ color: '#e53935' }}>*</span></label>
                      <textarea name="business_address" value={formData.business_address} onChange={handleChange} {...validationProps('business_address')} rows="2" required />
                      {renderFieldError('business_address')}
                    </div>
                  </>}
                  <div className="form-group">
                    <label>व्यवसायाशी तुमचे नाते</label>
                    <select
                      name="requested_business_relationship"
                      value={formData.requested_business_relationship}
                      onChange={handleChange}
                    >
                      <option value="owner">मालक / एकल स्वामित्व</option>
                      <option value="partner">भागीदार</option>
                      <option value="director">संचालक</option>
                      <option value="authorized_representative">अधिकृत प्रतिनिधी</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>गावाबाहेरील पत्रव्यवहाराचा पत्ता</label>
                    <textarea
                      name="external_address"
                      value={formData.external_address}
                      onChange={handleChange}
                      rows="3"
                      placeholder="तुमचा सध्याचा पत्रव्यवहाराचा पत्ता"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <fieldset className="form-group" style={{ gridColumn: '1 / -1', border: '1px solid #d7e3ef', borderRadius: 8, padding: 12 }}>
                    <legend style={{ fontWeight: 700, padding: '0 6px' }}>मागितलेला व्यवसाय खाते प्रवेश</legend>
                    <p style={{ margin: '0 0 10px', color: '#52677c', fontSize: 13 }}>
                      आवश्यक सुविधा निवडा. ग्रामपंचायत पुरावे तपासून यापेक्षा मर्यादित प्रवेश मंजूर करू शकते.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                      {BUSINESS_ACCESS_SCOPE_OPTIONS.map(([scope, label]) => (
                        <label key={scope} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontWeight: 500 }}>
                          <input
                            type="checkbox"
                            checked={formData.requested_access_scopes.includes(scope)}
                            onChange={() => toggleRequestedScope(scope)}
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                    {renderFieldError('requested_access_scopes')}
                  </fieldset>
                </div>
                <div className="reg-household-card">
                  प्रक्रिया: अर्ज सादर करा → GP अधिकारी ओळख व व्यवसाय नोंद तपासतील → आवश्यक असल्यास व्यवसाय नोंदवही तयार/दुरुस्त करतील → मर्यादित खाते प्रवेश मंजूर करतील → त्यानंतर व्यवसाय कर पाहता व भरता येईल.
                </div>
              </div>
            )}

            {/* Aadhaar — number on left, upload on right */}
            <div className="reg-section">
              <div className="reg-section-title">
                <span>🪨</span> आधार पडताळणी
              </div>
              <div className="reg-aadhaar-row">
                <div>
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label style={{ fontSize: 13 }}>आधार क्रमांक <span style={{ color: '#e53935' }}>*</span></label>
                    <input
                      type="text" name="aadhaar_number"
                      value={formatAadhaar(formData.aadhaar_number)}
                      onChange={handleChange} {...validationProps('aadhaar_number')} required placeholder="XXXX XXXX XXXX"
                      maxLength={14}
                      style={{ letterSpacing: 2, fontFamily: 'monospace', fontSize: 15, padding: '8px 10px' }}
                    />
                    {renderFieldError('aadhaar_number')}
                    <small style={{ color: '#888', fontSize: 11 }}>{formData.aadhaar_number.length}/१२ अंक</small>
                  </div>
                  <div style={{ background: '#e3f2fd', borderRadius: 5, padding: '6px 8px', fontSize: 11, color: '#1565c0', lineHeight: 1.4 }}>
                    The document is used only for identity review. External business taxpayers do not become village residents.
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>आधार फोटो <span style={{ color: '#e53935' }}>*</span></label>
                  <div
                    className="reg-upload-zone"
                    onClick={() => fileInputRef.current?.click()}
                    tabIndex={0}
                    role="button"
                    data-invalid={Boolean(fieldError('aadhaar_photo'))}
                    aria-describedby={fieldError('aadhaar_photo') ? 'aadhaar_photo-error' : undefined}
                    onBlur={() => touchField('aadhaar_photo')}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') fileInputRef.current?.click();
                    }}
                  >
                    {photoPreview ? (
                      <>
                        <img src={photoPreview} alt="Aadhaar" style={{ maxWidth: '100%', maxHeight: 100, borderRadius: 4 }} />
                        <span style={{ fontSize: 11, color: '#666', marginTop: 4 }}>{aadhaarPhoto?.name} — बदलण्यासाठी क्लिक करा</span>
                      </>
                    ) : aadhaarPhoto ? (
                      <>
                        <span style={{ fontSize: 24 }}>📄</span>
                        <span style={{ fontSize: 11, color: '#666' }}>{aadhaarPhoto.name} — बदलण्यासाठी क्लिक करा</span>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: 24 }}>📷</span>
                        <span style={{ fontSize: 12, color: '#888' }}>आधार फोटो अपलोड करा</span>
                        <span style={{ fontSize: 10, color: '#aaa' }}>JPG, PNG, PDF — जास्तीत जास्त 5MB</span>
                      </>
                    )}
                  </div>
                  {renderFieldError('aadhaar_photo')}
                  <input ref={fileInputRef} type="file" name="aadhaar_photo" accept=".jpg,.jpeg,.png,.pdf" onChange={handleFileChange} style={{ display: 'none' }} />
                </div>
              </div>
            </div>

            {/* Password — 2-col grid */}
            <div className="reg-section">
              <div className="reg-section-title">
                <span>🔒</span> खाते सुरक्षा
              </div>
              <div className="reg-row">
                <div className="form-group">
                  <label>पासवर्ड <span style={{ color: '#e53935' }}>*</span></label>
                  <div className="reg-input-wrap">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      className="reg-english-input"
                      value={formData.password}
                      onChange={handleChange}
                      {...validationProps('password')}
                      required
                      minLength={6}
                      placeholder="किमान ६ अक्षरे"
                    />
                    <button
                      type="button"
                      className="reg-password-toggle"
                      onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? 'पासवर्ड लपवा' : 'पासवर्ड दाखवा'}
                      title={showPassword ? 'पासवर्ड लपवा' : 'पासवर्ड दाखवा'}
                    >
                      <EyeIcon show={showPassword} />
                    </button>
                  </div>
                  {renderFieldError('password')}
                </div>
                <div className="form-group">
                  <label>पासवर्ड पुष्टी <span style={{ color: '#e53935' }}>*</span></label>
                  <div className="reg-input-wrap">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      className="reg-english-input"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      {...validationProps('confirmPassword')}
                      required
                      placeholder="पासवर्ड पुन्हा टाका"
                    />
                    <button
                      type="button"
                      className="reg-password-toggle"
                      onClick={() => setShowConfirmPassword(v => !v)}
                      aria-label={showConfirmPassword ? 'पासवर्ड लपवा' : 'पासवर्ड दाखवा'}
                      title={showConfirmPassword ? 'पासवर्ड लपवा' : 'पासवर्ड दाखवा'}
                    >
                      <EyeIcon show={showConfirmPassword} />
                    </button>
                  </div>
                  {renderFieldError('confirmPassword')}
                </div>
              </div>
            </div>

            {/* Notice */}
            <div className="reg-notice">
              <strong>📌 टीप:</strong> खाते ग्रामपंचायत प्रशासनाने मंजूर केल्यानंतरच सक्रिय होईल.
            </div>

            <div className="reg-section" aria-labelledby="registration-consent-title">
              <div className="reg-section-title" id="registration-consent-title"><span>⚖️</span> आवश्यक संमती व घोषणा</div>
              <label className="legal-check">
                <input type="checkbox" name="terms" checked={legalConsent.terms} onChange={event => setLegalConsent(current => ({ ...current, terms: event.target.checked }))} {...validationProps('terms')} required />
                <span>मी आवृत्ती {LEGAL_VERSION} च्या <Link to={`/gp/${tenantSlug}/legal/terms`} target="_blank">अटी व शर्ती</Link> वाचल्या असून त्या स्वीकारतो/स्वीकारते.</span>
              </label>
              {renderFieldError('terms')}
              <label className="legal-check">
                <input type="checkbox" name="privacy" checked={legalConsent.privacy} onChange={event => setLegalConsent(current => ({ ...current, privacy: event.target.checked }))} {...validationProps('privacy')} required />
                <span>मी <Link to={`/gp/${tenantSlug}/legal/privacy`} target="_blank">गोपनीयता व वैयक्तिक डेटा संरक्षण धोरण</Link> वाचले आहे. ओळख पडताळणी, खाते-सुरक्षा आणि मागितलेल्या शासकीय सेवेसाठी आवश्यक डेटाच्या प्रक्रियेबद्दल मला माहिती देण्यात आली आहे.</span>
              </label>
              {renderFieldError('privacy')}
              <label className="legal-check">
                <input type="checkbox" name="declaration" checked={legalConsent.declaration} onChange={event => setLegalConsent(current => ({ ...current, declaration: event.target.checked }))} {...validationProps('declaration')} required />
                <span>मी दिलेली माहिती खरी आहे, सादर केलेल्या ओळख/व्यवसाय कागदपत्रांचा अधिकृत वापर करण्याचा मला अधिकार आहे आणि खोटी माहिती, दुसऱ्याची तोतयागिरी किंवा अनधिकृत प्रवेश यासाठी कारवाई होऊ शकते हे मान्य करतो/करते.</span>
              </label>
              {renderFieldError('declaration')}
            </div>

            <button type="submit" className="btn btn-primary reg-submit" disabled={loading}>
              {loading ? 'सबमिट होत आहे...' : '📝 नोंदणी करा'}
            </button>
          </form>

          <p className="reg-footer">
            आधीच खाते आहे? <Link to="/login">लॉगिन करा</Link><br />
            <span className="legal-consent-links"><Link to={`/gp/${tenantSlug}/legal/terms`}>अटी</Link> · <Link to={`/gp/${tenantSlug}/legal/privacy`}>गोपनीयता</Link> · <Link to={`/gp/${tenantSlug}/legal/cookies`}>कुकीज</Link></span>
          </p>
        </div>
      </div>
      </div>
    </>
  
  );
}
